class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.triggerBindings = new WeakSet();
    this.handleDrawerClick = this.handleDrawerClick.bind(this);
    this.handleDrawerChange = this.handleDrawerChange.bind(this);
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
    this.handleDocumentCartClick = this.handleDocumentCartClick.bind(this);
    this.handleDocumentCartKeydown = this.handleDocumentCartKeydown.bind(this);

    this.addEventListener('click', this.handleDrawerClick);
    this.addEventListener('change', this.handleDrawerChange);
    this.addEventListener('keyup', this.handleKeyup);

    // Use delegated capture-phase listeners so the cart drawer keeps working
    // even when Shopify/header sections replace the cart icon after page load.
    document.addEventListener('click', this.handleDocumentCartClick, true);
    document.addEventListener('keydown', this.handleDocumentCartKeydown, true);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('wi:cart-item-added', this.handleCartUpdated);
    document.addEventListener('cart:updated', this.handleCartUpdated);
    document.addEventListener('cart:change', this.handleCartUpdated);

    this.bindCartTriggers();

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          const count = event?.cartData?.item_count;

          if (Number.isFinite(Number(count))) {
            this.updateCartCount(Number(count));
          }
        }
      );
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleDrawerClick);
    this.removeEventListener('change', this.handleDrawerChange);
    this.removeEventListener('keyup', this.handleKeyup);

    document.removeEventListener('click', this.handleDocumentCartClick, true);
    document.removeEventListener('keydown', this.handleDocumentCartKeydown, true);
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    document.removeEventListener('wi:cart-item-added', this.handleCartUpdated);
    document.removeEventListener('cart:updated', this.handleCartUpdated);
    document.removeEventListener('cart:change', this.handleCartUpdated);

    this.cartUpdateUnsubscriber?.();
  }

  get triggerSelector() {
    return [
      '#cart-icon-bubble',
      '[data-wi-cart-drawer-trigger]',
      '.wi-header__icon-link--cart',
      '.wi-header__mobile-drawer-action[href*="/cart"]'
    ].join(',');
  }

  bindCartTriggers(scope = document) {
    scope.querySelectorAll?.(this.triggerSelector).forEach((trigger) => {
      if (this.triggerBindings.has(trigger)) return;

      this.triggerBindings.add(trigger);
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'CartDrawer');
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  handleDocumentCartClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest(this.triggerSelector);

    if (!trigger) return;

    // Never allow a matching cart control to fall through to /cart.
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open(trigger);
  }

  handleDocumentCartKeydown(event) {
    if (event.code !== 'Space' && event.code !== 'Enter') return;

    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest(this.triggerSelector);

    if (!trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.open(trigger);
  }

  handleSectionLoad(event) {
    this.bindCartTriggers(event.target || document);
  }

  handleDrawerClick(event) {
    const variantToggle = event.target.closest('[data-wi-rec-variant-toggle]');

    if (variantToggle) {
      event.preventDefault();
      this.showRecommendationVariants(variantToggle);
      return;
    }

    const quickAddButton = event.target.closest('[data-wi-rec-add]');

    if (quickAddButton) {
      event.preventDefault();
      this.addRecommendationVariant(quickAddButton.dataset.variantId, quickAddButton);
      return;
    }

    if (
      event.target.closest('[data-cart-drawer-close]') ||
      event.target.id === 'CartDrawer-Overlay'
    ) {
      event.preventDefault();
      this.close();
    }
  }

  handleDrawerChange(event) {
    const variantSelect = event.target.closest('[data-wi-rec-variant-select]');

    if (!variantSelect || !variantSelect.value) return;

    this.addRecommendationVariant(variantSelect.value, variantSelect);
  }

  showRecommendationVariants(button) {
    const card = button.closest('[data-wi-rec-card]');
    if (!card) return;

    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? this.querySelector(`#${panelId}`) : null;
    const select = panel?.querySelector('[data-wi-rec-variant-select]');

    if (!panel || !select) return;

    // Keep the interaction clean: only one Bestseller variant picker stays
    // open at a time. Other cards return to their normal Add to cart CTA.
    this.querySelectorAll('[data-wi-rec-variant-panel]').forEach((otherPanel) => {
      if (otherPanel === panel) return;

      otherPanel.hidden = true;
      const otherCard = otherPanel.closest('[data-wi-rec-card]');
      const otherToggle = otherCard?.querySelector('[data-wi-rec-variant-toggle]');
      otherCard?.classList.remove('is-variant-open');

      if (otherToggle) {
        otherToggle.hidden = false;
        otherToggle.setAttribute('aria-expanded', 'false');
      }
    });

    button.hidden = true;
    button.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    card.classList.add('is-variant-open');

    window.requestAnimationFrame(() => select.focus());
  }

  async addRecommendationVariant(variantId, control) {
    const id = Number.parseInt(variantId, 10);

    if (!Number.isFinite(id) || id <= 0 || control?.dataset?.wiAdding === 'true') {
      return;
    }

    const card = control.closest('[data-wi-rec-card]');
    const originalButtonText = control.tagName === 'BUTTON' ? control.textContent : '';

    control.dataset.wiAdding = 'true';
    control.disabled = true;
    card?.classList.add('is-adding');

    if (control.tagName === 'BUTTON') {
      control.textContent = 'Adding…';
    }

    const shopifyRoot =
      window.Shopify?.routes?.root ||
      '/';
    const normalizedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
    const cartAddUrl = `${normalizedRoot}cart/add.js`;
    const sections = this.getSectionsToRender().map((section) => section.id);

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id,
              quantity: 1
            }
          ],
          sections,
          sections_url: window.location.pathname
        })
      });

      const parsedState = await response.json().catch(() => ({}));

      if (!response.ok || parsedState.status) {
        throw new Error(
          parsedState.description ||
          parsedState.message ||
          'Could not add this product to the cart.'
        );
      }

      // renderContents swaps in Shopify's freshly rendered cart drawer.
      // That simultaneously shows the new cart item, refreshes subtotal/count,
      // and removes this product from the Bestseller rail because Liquid
      // excludes products already present in cart.items.
      this.renderContents(parsedState);
    } catch (error) {
      console.warn('[WI Cart Drawer] Bestseller quick add failed.', error);

      control.dataset.wiAdding = 'false';
      control.disabled = false;
      card?.classList.remove('is-adding');

      if (control.tagName === 'BUTTON') {
        control.textContent = 'Try again';

        window.setTimeout(() => {
          if (!control.isConnected || control.dataset.wiAdding === 'true') return;
          control.textContent = originalButtonText || 'Add to cart';
        }, 1600);
      } else {
        control.value = '';
        control.classList.add('has-error');

        window.setTimeout(() => {
          control.classList.remove('has-error');
        }, 1600);
      }
    }
  }

  handleKeyup(event) {
    if (event.code === 'Escape') {
      this.close();
    }
  }

  handleCartUpdated(event) {
    const detailCount =
      event?.detail?.cart?.item_count ??
      event?.detail?.item_count ??
      event?.cartData?.item_count;

    if (Number.isFinite(Number(detailCount))) {
      this.updateCartCount(Number(detailCount));
      this.open();
      return;
    }

    this.fetchCartState().then((cart) => {
      if (!cart) return;
      this.updateCartCount(cart.item_count);
      this.open();
    });
  }

  closeMobileDrawer(triggeredBy) {
    const mobileMenu = triggeredBy?.closest('[data-wi-header-mobile-menu]');

    if (mobileMenu?.open) {
      mobileMenu.open = false;
      document.documentElement.classList.remove('wi-mobile-menu-open');
    }
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      this.closeMobileDrawer(triggeredBy);
    }

    if (this.classList.contains('active')) return;

    this.bindCartTriggers();

    this.classList.add('animate');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('active');
      });
    });

    document.body.classList.add('overflow-hidden');

    document.querySelectorAll(this.triggerSelector).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');

    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    let didFocusDrawer = false;

    const focusDrawer = () => {
      if (didFocusDrawer) return;
      didFocusDrawer = true;

      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner')
        : this.querySelector('#CartDrawer');

      const focusElement =
        this.querySelector('.drawer__close') ||
        this.querySelector('.drawer__inner');

      if (container && focusElement && typeof trapFocus === 'function') {
        trapFocus(container, focusElement);
      } else {
        focusElement?.focus?.();
      }
    };

    this.querySelector('.drawer__inner')?.addEventListener(
      'transitionend',
      focusDrawer,
      { once: true }
    );

    window.setTimeout(focusDrawer, 360);

    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    document.querySelectorAll(this.triggerSelector).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    } else {
      this.activeElement?.focus?.();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const controlledElement = cartDrawerNote.nextElementSibling;

    if (controlledElement?.id) {
      cartDrawerNote.setAttribute('aria-controls', controlledElement.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');
      event.currentTarget.setAttribute(
        'aria-expanded',
        String(!details?.hasAttribute('open'))
      );
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement?.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  async fetchCartState() {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.warn('[WI Cart Drawer] Cart refresh failed.', error);
      return null;
    }
  }

  updateCartCount(value) {
    const count = Math.max(0, Number.parseInt(value, 10) || 0);

    this.classList.toggle('is-empty', count === 0);

    this.querySelectorAll('[data-wi-drawer-count]').forEach((element) => {
      element.dataset.count = String(count);
      element.innerHTML = `(<strong>${count}</strong> ${count === 1 ? 'item' : 'items'})`;
    });

    document
      .querySelectorAll(
        [
          '.wi-header__icon-link--cart',
          '.wi-header__mobile-drawer-action[href*="/cart"]',
          '[data-wi-cart-drawer-trigger]'
        ].join(',')
      )
      .forEach((trigger) => {
        let badge = trigger.querySelector('.wi-header__cart-count');

        if (!badge && count > 0) {
          badge = document.createElement('span');
          badge.className = 'wi-header__cart-count';
          badge.setAttribute('aria-live', 'polite');
          trigger.appendChild(badge);
        }

        if (!badge) return;

        badge.textContent = count > 99 ? '99+' : String(count);
        badge.hidden = count === 0;
        badge.classList.toggle('hidden', count === 0);
      });

    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      bubble.hidden = count === 0;
      bubble.querySelectorAll('span').forEach((span, index) => {
        if (index === 0) span.textContent = String(count);
      });
    });
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    const sectionHTML = parsedState.sections?.['cart-drawer'];
    let renderedCount = null;

    if (sectionHTML) {
      const sectionDocument = new DOMParser().parseFromString(
        sectionHTML,
        'text/html'
      );
      const sourceDrawer = sectionDocument.querySelector('cart-drawer');
      const source = sectionDocument.querySelector('#CartDrawer');
      const target = this.querySelector('#CartDrawer');

      if (source && target) {
        target.innerHTML = source.innerHTML;
      }

      // The add-to-cart Ajax response does not include item_count, but the
      // freshly rendered cart-drawer section does. Read it immediately so the
      // root .is-empty state is correct before the drawer is opened.
      const sourceCount = source?.querySelector('[data-wi-drawer-count]');
      const countFromSection = Number(sourceCount?.dataset?.count);

      if (Number.isFinite(countFromSection)) {
        renderedCount = countFromSection;
      } else if (sourceDrawer) {
        renderedCount = sourceDrawer.classList.contains('is-empty') ? 0 : null;
      }
    }

    const cartIconHTML = parsedState.sections?.['cart-icon-bubble'];

    if (cartIconHTML) {
      const sourceIcon = this.getSectionDOM(cartIconHTML, '#cart-icon-bubble');
      const targetIcon = document.getElementById('cart-icon-bubble');

      if (sourceIcon && targetIcon) {
        targetIcon.replaceWith(sourceIcon);
      }
    }

    const directCount = Number(parsedState.item_count);

    if (Number.isFinite(directCount)) {
      this.updateCartCount(directCount);
    } else if (Number.isFinite(renderedCount)) {
      this.updateCartCount(renderedCount);
    } else {
      this.fetchCartState().then((cart) => {
        if (cart) this.updateCartCount(cart.item_count);
      });
    }

    this.bindCartTriggers();

    window.setTimeout(() => {
      this.open();
    }, 20);
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      }
    ];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble'
      });
    }

    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;

    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      }
    ];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      });
    }

    return sections;
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
